import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Zap, MapPin, Building2, Truck, Fuel, Palmtree } from "lucide-react";
import { IMG_RESORT_CHARGER, IMG_HMEL_HIGHWAY, IMG_CHARGER_CLOSEUP, IMG_EV_CHARGING, IMG_RESORT_LIFESTYLE, IMG_MODERN_CHARGER } from "@/lib/images";
import { NetworkMap } from "@/components/network/NetworkMap";

const HERO_IMAGE = "/generated/e3_highway_hub.png";
const RESORT_IMAGE = "/generated/e3_resort_charger.png";
const CHARGER_CLOSEUP = "/generated/e3_premium_hub.png";

const DEPLOYMENTS = [
    {
        img: IMG_RESORT_CHARGER,
        location: "Avantika Resort · Dahod, Gujarat",
        headline: "60 kW DC Fast Charger",
        subtitle: "Destination Charging · NH 47 corridor",
        objectPosition: "50% 30%",
    },
    {
        img: IMG_HMEL_HIGHWAY,
        location: "HMEL · Sector 108, Noida",
        headline: "120 kW Highway Charger",
        subtitle: "Strategic partnership with HPCL-Mittal Energy",
        objectPosition: "50% 55%",
    },
    {
        img: IMG_CHARGER_CLOSEUP,
        location: "GERMI EV Roadshow · Gujarat",
        headline: "60 kW DC Innovation Demo",
        subtitle: "EV Cosmos DC Fast Charger showcase",
        objectPosition: "20% 45%",
    },
];

const CATEGORIES = [
    { icon: Zap, title: "Highway Charging Hubs", desc: "Fast charging every 100 km across India's major highways." },
    { icon: Palmtree, title: "Premium Hotels & Resorts", desc: "Destination charging for premium EV travellers." },
    { icon: Fuel, title: "Fuel Station Integration", desc: "Bringing next-gen charging alongside existing infrastructure." },
    { icon: Truck, title: "Fleet Charging Hubs", desc: "Purpose-built infrastructure for logistics and taxi fleets." },
    { icon: Building2, title: "Commercial Real Estate", desc: "Adding ESG value to commercial properties." },
    { icon: MapPin, title: "Pan-India Network", desc: "500 stations, 1,000+ chargers, 100+ cities in Year One." },
];

const MARQUEE_ITEMS = ["500 Stations", "1,000+ Chargers", "2,000 Guns", "100+ Cities", "120 kW — 360 kW", "99% Uptime", "Founding Partner Program", "E3 Capital Connect™"];

export const Home = () => {
    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-[92vh] flex items-end overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${HERO_IMAGE})` }}
                    aria-hidden="true"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/85 via-stone-950/45 to-stone-950/10" aria-hidden="true" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28 w-full">
                    <div className="max-w-4xl">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs tracking-[0.2em] uppercase mb-8 animate-fade-up">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            India's Premium High-Speed Charging Network
                        </div>
                        <h1 data-testid="home-hero-title" className="font-serif text-white text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] mb-8 animate-fade-up">
                            Powering<br />
                            Tomorrow,<br />
                            <span className="italic e3-gradient-text">Naturally.</span>
                        </h1>
                        <p className="text-white/80 text-lg md:text-xl max-w-2xl leading-relaxed mb-10 animate-fade-up">
                            Building India's next-generation network of 120 kW to 360 kW fast
                            charging stations — designed for long-range EVs, premium
                            destinations and the decade ahead.
                        </p>
                        <div className="flex flex-wrap gap-4 animate-fade-up">
                            <Link to="/investor" data-testid="home-cta-investor" className="e3-btn-primary">
                                Explore Investor Opportunity <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link to="/franchise" data-testid="home-cta-franchise" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm text-white bg-stone-950/60 backdrop-blur-lg border border-white/50 hover:bg-stone-950/80 shadow-xl transition">
                                Become a Franchise Partner <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* MARQUEE */}
            <section className="border-y border-stone-200 py-8 overflow-hidden bg-stone-50">
                <div className="marquee-track font-serif text-2xl md:text-3xl text-stone-400">
                    {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
                        <span key={i} className="flex items-center gap-16">
                            {item} <span className="text-emerald-500">◆</span>
                        </span>
                    ))}
                </div>
            </section>

            {/* WHY NOW */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <div className="e3-overline mb-6">Why Now</div>
                        <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900 mb-8">
                            India's EV market is entering <span className="italic e3-gradient-text">hyper growth.</span>
                        </h2>
                        <p className="text-stone-600 text-lg leading-relaxed mb-6">
                            Passenger EV sales are accelerating. Battery sizes are increasing.
                            Range is increasing. Consumer expectations have changed.
                        </p>
                        <p className="text-stone-600 text-lg leading-relaxed">
                            Drivers no longer accept slow charging or waiting hours. They want
                            <span className="text-stone-900 font-medium"> fast, reliable, available.</span>
                        </p>
                    </div>
                    <div className="md:col-span-7 grid grid-cols-2 gap-6">
                        <div className="e3-card-static space-y-2 col-span-2 md:col-span-1">
                            <div className="e3-overline">2030 Target</div>
                            <div className="font-serif text-5xl md:text-6xl font-light text-stone-900">1.32M</div>
                            <div className="text-sm text-stone-600">Public charging stations India requires by 2030.</div>
                        </div>
                        <div className="e3-card-static space-y-2">
                            <div className="e3-overline">Sector CAGR</div>
                            <div className="font-serif text-5xl md:text-6xl font-light e3-gradient-text">28–31%</div>
                            <div className="text-sm text-stone-600">Projected EV charging market growth through 2030.</div>
                        </div>
                        <div className="e3-card-static space-y-2 col-span-2">
                            <div className="e3-overline">Current Reality</div>
                            <div className="font-serif text-4xl md:text-5xl font-light text-stone-900">1 : 235</div>
                            <div className="text-sm text-stone-600">Only one public charging station for every 235 EVs in India today.</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* NETWORK VISION */}
            <section className="py-24 md:py-32 bg-stone-950 text-white px-6 md:px-10 relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #6abf4b 0px, transparent 50%), radial-gradient(circle at 70% 80%, #2b8fb0 0px, transparent 50%)" }} />
                <div className="max-w-7xl mx-auto relative">
                    <div className="e3-overline mb-6 text-stone-400">Network Vision · Year One</div>
                    <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] max-w-3xl mb-16">
                        Building one of India's largest privately developed charging networks.
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 stagger">
                        {[
                            { n: "500", l: "Stations" },
                            { n: "1,000+", l: "Fast Chargers" },
                            { n: "2,000", l: "Charging Guns" },
                            { n: "100+", l: "Cities" },
                        ].map((s, i) => (
                            <div key={i} className="border-t border-stone-800 pt-6">
                                <div className="font-serif text-5xl md:text-6xl font-light e3-gradient-text mb-3">{s.n}</div>
                                <div className="text-stone-400 text-sm tracking-wide">{s.l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* NETWORK MAP — tentative rollout */}
            <section className="py-24 md:py-32 px-6 md:px-10 bg-stone-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
                        <div className="md:col-span-7">
                            <div className="e3-overline mb-6">Tentative Network Map</div>
                            <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900">
                                Gujarat to <span className="italic e3-gradient-text">Punjab</span> and beyond.
                            </h2>
                        </div>
                        <div className="md:col-span-5">
                            <p className="text-stone-600 text-lg leading-relaxed">
                                A phased 24-month rollout — starting in the E3 home state of Gujarat,
                                extending along the western corridor into Maharashtra & Madhya Pradesh,
                                then moving north to Rajasthan, Delhi NCR, and densifying deepest in Punjab and above.
                            </p>
                        </div>
                    </div>
                    <NetworkMap />
                </div>
            </section>

            {/* CATEGORIES */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
                        <div className="md:col-span-7">
                            <div className="e3-overline mb-6">Where We Build</div>
                            <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900">
                                A charger for every <span className="italic e3-gradient-text">journey.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-5">
                            <p className="text-stone-600 text-lg leading-relaxed">
                                From national highways to premium resorts, from fleet depots to
                                commercial properties — E3 is engineered for the decade ahead.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
                        {CATEGORIES.map((cat, i) => {
                            const Icon = cat.icon;
                            return (
                                <div key={i} className="e3-card group" data-testid={`home-category-${i}`}>
                                    <div className="w-12 h-12 rounded-xl e3-gradient-soft flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                        <Icon className="w-6 h-6 text-emerald-600" strokeWidth={1.5} />
                                    </div>
                                    <h3 className="font-serif text-2xl font-medium text-stone-900 mb-3">{cat.title}</h3>
                                    <p className="text-stone-600 leading-relaxed">{cat.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SPLIT: DESTINATION */}
            <section className="py-24 md:py-32 px-6 md:px-10 bg-stone-50">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-6 order-2 md:order-1">
                        <div className="e3-overline mb-6">Destination Charging</div>
                        <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tighter leading-[1.05] text-stone-900 mb-8">
                            Great stays deserve great infrastructure.
                        </h2>
                        <p className="text-stone-600 text-lg leading-relaxed mb-6">
                            Hotels, resorts and premium destinations become gateways to a new
                            kind of EV tourism. Additional footfall. F&B revenue. ESG credentials.
                            A charger for every guest.
                        </p>
                        <Link to="/franchise" className="e3-btn-ghost">
                            Explore Destination Model <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="md:col-span-6 order-1 md:order-2">
                        <div className="rounded-2xl overflow-hidden aspect-[4/5] md:aspect-[4/5]">
                            <img src={RESORT_IMAGE} alt="Premium destination charging" className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* TECH PARTNER */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-6">
                        <div className="rounded-2xl overflow-hidden aspect-[5/4]">
                            <img src={CHARGER_CLOSEUP} alt="Modern EV charger" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <div className="e3-overline mb-6">Technology Backbone</div>
                        <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tighter leading-[1.05] text-stone-900 mb-8">
                            Powered by <span className="italic e3-gradient-text">Cubenz Technology™</span>
                        </h2>
                        <p className="text-stone-600 text-lg leading-relaxed mb-8">
                            Direct OEM support. Chargers, software, integration, maintenance,
                            energy management — E3 is built with an infrastructure OEM as its
                            technology partner, not a third-party reseller.
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                            {["Chargers 120–360 kW", "CMS Software", "Site Engineering", "OEM Service", "Energy Management", "Network Design"].map((f) => (
                                <div key={f} className="text-sm text-stone-700 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {f}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* REAL DEPLOYMENTS */}
            <section className="py-24 md:py-32 px-6 md:px-10 bg-stone-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
                        <div className="md:col-span-7">
                            <div className="e3-overline mb-6">Real Deployments</div>
                            <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900">
                                Not concept renders. <span className="italic e3-gradient-text">Actual chargers, on ground.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-5">
                            <p className="text-stone-600 text-lg leading-relaxed">
                                Live installations across Gujarat, Uttar Pradesh and beyond —
                                delivered by our OEM technology partner Cubenz.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger">
                        {DEPLOYMENTS.map((d, i) => (
                            <div key={i} className="group rounded-2xl overflow-hidden border border-stone-200 bg-white transition-all hover:-translate-y-1 hover:shadow-2xl" data-testid={`home-deployment-${i}`}>
                                <div className="aspect-[4/3] overflow-hidden bg-stone-100">
                                    <img
                                        src={d.img}
                                        alt={d.headline}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        style={{ objectPosition: d.objectPosition }}
                                    />
                                </div>
                                <div className="p-6">
                                    <div className="e3-overline mb-3">{d.location}</div>
                                    <h3 className="font-serif text-2xl font-medium text-stone-900 mb-2 leading-tight">{d.headline}</h3>
                                    <p className="text-sm text-stone-600">{d.subtitle}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-6xl mx-auto e3-gradient-bg rounded-3xl p-12 md:p-20 text-white relative overflow-hidden">
                    <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
                    <div className="relative">
                        <div className="e3-overline mb-6 text-white/70">Join the Network</div>
                        <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] mb-6 max-w-3xl">
                            Own the infrastructure. Build recurring income.
                        </h2>
                        <p className="text-white/85 text-lg md:text-xl max-w-2xl mb-10">
                            Invest once. Build an infrastructure asset. Participate in India's
                            electric mobility transition for the next decade.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <Link to="/investor" data-testid="home-final-cta-investor" className="bg-white text-stone-900 px-8 py-3.5 rounded-full font-semibold hover:bg-stone-100 transition inline-flex items-center gap-2">
                                Investor Opportunity <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link to="/franchise" data-testid="home-final-cta-franchise" className="bg-white/10 border border-white/30 text-white px-8 py-3.5 rounded-full font-semibold hover:bg-white/20 transition inline-flex items-center gap-2">
                                Franchise Models <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};
